import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { COUNTIES } from "@/data/content";

const CARE = ["Agency nursing staff", "Complex care", "Other"];
const URGENCY = ["Today", "This week", "Planning ahead"];
const EMPTY = { name: "", email: "", phone: "", organisation: "", care_type: "", county: "", urgency: "This week", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="eyebrow !text-[11px] text-slate-500">{label}</span>
    {children}
  </label>
);

export const EnquiryForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, { ...f, organisation: f.organisation || null });
      setDone(data?.reference ?? "received");
      setF(EMPTY);
      toast.success("Enquiry received — we'll be in touch shortly.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? `Please check: ${d.map((x) => x?.loc?.slice(-1)[0]).join(", ")}` : "Something went wrong. Please call us instead.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} data-testid="enquiry-success-message" className="rounded-3xl bg-white p-10 md:p-14 border border-slate-200">
        <CheckCircle2 className="text-emerald-600" size={44} />
        <h3 className="mt-6 text-3xl font-extrabold tracking-tight text-[#0A192F]">Thank you. We're on it.</h3>
        <p className="mt-3 text-slate-600">A coordinator will call you back shortly. Your reference is <span className="font-mono-x font-medium text-[#0A192F]" data-testid="enquiry-reference">{done}</span>.</p>
        <button data-testid="btn-new-enquiry" onClick={() => setDone(null)} className="mt-8 font-semibold text-sky-700 border-b-2 border-sky-500 pb-1">Send another enquiry</button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="care-enquiry-form" className="rounded-3xl bg-white p-6 sm:p-10 md:p-12 border border-slate-200 grid sm:grid-cols-2 gap-x-8 gap-y-7">
      <div className="sm:col-span-2">
        <span className="eyebrow !text-[11px] text-slate-500">How urgent is it?</span>
        <div className="mt-3 flex flex-wrap gap-2">
          {URGENCY.map((u) => (
            <button type="button" key={u} data-testid={`urgency-${u.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setF({ ...f, urgency: u })}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors ${f.urgency === u ? "bg-[#0A192F] text-white border-[#0A192F]" : "border-slate-300 text-slate-700 hover:border-[#0A192F]"}`}>
              {u === "Today" && <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle" />}{u}
            </button>
          ))}
        </div>
      </div>
      <Field label="Your name *"><input required minLength={2} className="field" value={f.name} onChange={set("name")} data-testid="input-name" /></Field>
      <Field label="Organisation (optional)"><input className="field" value={f.organisation} onChange={set("organisation")} data-testid="input-organisation" placeholder="Ward, care home or leave blank" /></Field>
      <Field label="Email *"><input required type="email" className="field" value={f.email} onChange={set("email")} data-testid="input-email" /></Field>
      <Field label="Phone *"><input required minLength={6} type="tel" className="field" value={f.phone} onChange={set("phone")} data-testid="input-phone" /></Field>
      <Field label="Type of care *">
        <select required className="field" value={f.care_type} onChange={set("care_type")} data-testid="select-care-type">
          <option value="" disabled>Choose…</option>
          {CARE.map((c) => <option key={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="County *">
        <select required className="field" value={f.county} onChange={set("county")} data-testid="select-county">
          <option value="" disabled>Choose…</option>
          {[...COUNTIES, "Other"].map((c) => <option key={c}>{c}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Tell us a little more"><textarea rows={3} className="field resize-none" value={f.message} onChange={set("message")} data-testid="input-message" placeholder="Shift dates, care needs, anything helpful" /></Field>
      </div>
      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-5 pt-2">
        <p className="text-xs text-slate-500 max-w-xs">We only use your details to respond to this enquiry.</p>
        <button disabled={busy} type="submit" data-testid="btn-submit-enquiry"
          className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#0A192F] text-white pl-7 pr-2 py-2 font-semibold hover:bg-sky-700 transition-colors disabled:opacity-60">
          {busy ? "Sending…" : "Send enquiry"}
          <span className="w-10 h-10 rounded-full bg-sky-400 text-[#0A192F] grid place-items-center transition-transform duration-300 group-hover:rotate-45">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <ArrowUpRight size={18} />}
          </span>
        </button>
      </div>
    </form>
  );
};
